import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import AbcIcon from '@mui/icons-material/Abc'
import type { LessonParticipant, LessonWordAddResult, LessonWordGroup } from '@/types'
import { lessonWordsApi } from '@/api/lessonWords'
import { getErrorMessage } from '@/utils/error'
import { lessonWordOriginal } from './lessonWordUtils'
import {
  LessonWordAddForm,
  LessonWordCardAlreadyExistedAlert,
  LessonWordSkippedAlert,
} from './LessonWordAddForm'
import { LessonWordList } from './LessonWordList'

type LessonWordsPanelProps = {
  lessonId: number
  lessonStatus: string
  participants: LessonParticipant[]
  isLessonTeacher: boolean
  words: LessonWordGroup[]
  loading: boolean
  error: string | null
  onRefresh: () => Promise<void>
}

export function LessonWordsPanel({
  lessonId,
  lessonStatus,
  participants,
  isLessonTeacher,
  words,
  loading,
  error,
  onRefresh,
}: LessonWordsPanelProps) {
  const { t } = useTranslation()
  const [deletingWordId, setDeletingWordId] = useState<number | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [skipped, setSkipped] = useState<LessonWordAddResult['skipped']>([])
  const [cardExistedTexts, setCardExistedTexts] = useState<string[]>([])

  const canAdd = isLessonTeacher && lessonStatus === 'in_progress'
  const canDelete = canAdd
  const canEdit = isLessonTeacher && (lessonStatus === 'in_progress' || lessonStatus === 'completed')
  const showWordDetails = isLessonTeacher || lessonStatus === 'completed'

  const handleAdded = async (result: LessonWordAddResult & { message?: string }) => {
    setActionError(null)
    setSkipped(result.skipped)
    const existed = new Set<string>()
    for (const item of result.created) {
      if (item.card_already_existed) {
        existed.add(lessonWordOriginal(item.word))
      }
    }
    setCardExistedTexts([...existed])
    await onRefresh()
  }

  const handleDelete = async (item: LessonWordGroup) => {
    setDeletingWordId(item.word_id)
    setActionError(null)
    try {
      await lessonWordsApi.removeByWord(lessonId, item.word_id)
      await onRefresh()
      setActionError(null)
    } catch (err) {
      setActionError(getErrorMessage(err) || t('common.error'))
    } finally {
      setDeletingWordId(null)
    }
  }

  return (
    <Card sx={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AbcIcon fontSize="small" sx={{ color: 'primary.main' }} />
          <Typography variant="h6" component="div" fontWeight={700}>
            {t('lessonWords.title')}
          </Typography>
          <Tooltip title={t('lessonWords.description')}>
            <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          </Tooltip>
        </Box>

        {canAdd && (
          <LessonWordAddForm
            lessonId={lessonId}
            participants={participants}
            onAdded={(result) => void handleAdded(result)}
          />
        )}

        <LessonWordSkippedAlert skipped={skipped} />
        <LessonWordCardAlreadyExistedAlert texts={cardExistedTexts} />

        {(error || actionError) && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setActionError(null)}
          >
            {error ?? actionError}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} variant="rounded" height={36} />
            ))}
          </Box>
        ) : (
          <LessonWordList
            items={words}
            canDelete={canDelete}
            canEdit={canEdit}
            deletingWordId={deletingWordId}
            showWordDetails={showWordDetails}
            emptyMessage={isLessonTeacher ? undefined : 'lessonWords.studentEmpty'}
            onDelete={(item) => void handleDelete(item)}
            onEdited={onRefresh}
          />
        )}
      </CardContent>
    </Card>
  )
}
